import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Toast, Skeleton } from 'antd-mobile'

import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'

import { auth, db, functions, storage } from '../utils/firebase'

auth.useDeviceLanguage()

const handleLogin = async (email, password) => {
  try {
    await signInWithEmailAndPassword(auth, email, password)
  } catch (error) {
    Toast.show({
      content: error.message
    })
  }
}

const handleLogout = async () => {
  try {
    await signOut(auth)
  } catch (error) {
    Toast.show({
      content: error.message
    })
  }
}

const AuthContext = React.createContext({
  auth,
  db,
  functions,
  storage,
  firebaseLogin: handleLogin,
  firebaseLogout: handleLogout,
  userId: '',
  user: auth.currentUser,
  role: '',
  // speechToken: {}
})

const AuthContextProvider = ({
  children
}) => {
  const navigate = useNavigate()
  const tempUser = auth.currentUser
  const [userId, setUserId] = useState()
  const [user, setUser] = useState(tempUser)
  const [role, setRole] = useState('')
  // const [speechToken, setSpeechToken] = useState({})
  const [loaded, setLoaded] = useState(false)
  const contextValue = {
    auth,
    db,
    functions,
    storage,
    firebaseLogin: handleLogin,
    firebaseLogout: handleLogout,
    userId,
    user,
    role,
    // speechToken
  }

  const getUserData = async (id) => {
    try {
      const snap = await getDoc(doc(db, 'users', id))
      const data = {
        ...snap.data(),
        id: snap.id
      }
      return Promise.resolve(data)
    } catch (error) {
      return Promise.reject(error)
    }
  }

  useEffect(() => {
    const staticPage = ['/', '/login', '/confirm-password', '/forget-password', '/reset-password']
    const dynamicPage = ['/register', '/auth/action']
    const prevUrl = window.location.pathname
    const unsubscribe = onAuthStateChanged(auth, (async (userAuth) => {
      if (!userAuth?.emailVerified) {
        if (userAuth && !userAuth.emailVerified) {
          Toast.show({
            content: 'คุณยังไม่ได้ Verify Email'
          })
        }
        if (dynamicPage.indexOf(prevUrl) !== -1) {
          navigate(prevUrl + window.location.search)
        } else if (staticPage.indexOf(prevUrl) === -1) {
          navigate(`/login?continue=${encodeURIComponent(prevUrl)}`)
        } else if (prevUrl === '/') {
          navigate('/login')
        } else {
          navigate(prevUrl)
        }
        setLoaded(true)
        return
      }

      const idToken = await userAuth.getIdTokenResult()
      const data = await getUserData(userAuth.uid)

      setUserId(data.id)
      setUser({ ...data })
      setRole(idToken?.claims?.role ?? '')

      // const getToken = await fetch('https://asia-southeast1-genji-dev.cloudfunctions.net/getSpeechToken-getSpeechToken')
      // const tokens = await getToken.json()
      // setSpeechToken(tokens)

      const url = new URL(window.location)
      const params = new URLSearchParams(url.search)
      const redirect = params.get('continue')

      if (redirect) {
        navigate(redirect)
      } else if (staticPage.indexOf(prevUrl) !== -1) {
        switch (idToken.claims.role) {
          case 'superadmin':
            navigate('/admin/conversations')
            break
          default:
            navigate('/admin/conversations')
        }
      }
      setLoaded(true)
    }))
    return () => {
      unsubscribe()
    }
  }, [navigate, role])

  if (!loaded) return <Skeleton></Skeleton>
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('Cannot useAuth outside AuthContextProvider!')
  }
  return context
}

export default AuthContextProvider
export {
  useAuth
}
